import { Add, CreateOutlined } from "@mui/icons-material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { Sheet } from "react-modal-sheet";
import { useLocation, useNavigate } from "react-router-dom";
import { collectionPath, EnvContext } from "../../context/EnvContext";
import SessionReplayContext from "../../context/SessionReplayContext";
import { UserContext } from "../../context/UserContext";
import AddressBox from "../../elements/AddressBox.jsx";
import AddressForm from "../../elements/AddressForm";
import FAQ from "../../elements/FAQ";
import Footer from "../../elements/Footer";
import FPButton from "../../elements/FPButton.jsx";
import Header from "../../elements/Header";
import ShippingCheckoutBanner from "../../elements/ShippingCheckoutBanner";
import ShippingOptions from "../../elements/ShippingOptions";
import StickyFooter from "../../elements/StickyFooter.jsx";
import Summary from "../../elements/Summary";
import { useFirestore } from "../../firebase/FirestoreContext";
import noop from "../../functions/noop";
import useCart from "../../hooks/useCart";
import useCartValidations from "../../hooks/useCartValidations";
import analyticsHelper from "../../utils/analyticsHelper";
import apiClient from "../../utils/ApiClient";
import CheckoutAuthBanner from "../../elements/CheckoutAuthBanner.jsx";

const zelleHandle = "@getfullproof";
const venmoHandle = "@getfullproof";

const ManualCheckout = () => {
  const { validateCart } = useCartValidations();
  const {
    summary,
    appliedDiscount,
    cartItems,
    wallet,
    cart,
    refresh,
    refreshedAtTime,
    selectedShippingData,
    setSelectedShippingData,
  } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const db = useFirestore();
  const firestore = useFirestore();
  const { user, loading } = React.useContext(UserContext);
  const environment = useContext(EnvContext);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const [shippingAddress, setShippingAddress] = useState();
  const [shippingAddressValid, setShippingAddressValid] = useState(false);

  const [showStickyFooter, setShowStickyFooter] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("zelle");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const payButtonRef = useRef(null);

  const selectedAddress = shippingAddressValid
    ? shippingAddress
    : addresses[selectedAddressIndex];

  const { enableSessionReplay } = useContext(SessionReplayContext);

  const { id: cartID } = cart || {};
  const { cart_sooperstock_ID = null } = location.state || {};

  const [isOpen, setOpen] = useState(false);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [tempSelectedAddressIndex, setTempSelectedAddressIndex] = useState(0);

  useEffect(() => {
    enableSessionReplay();
  }, [enableSessionReplay]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAddressSelect = (index) => {
    setTempSelectedAddressIndex(index);
  };

  const handleSaveAddressChange = () => {
    setSelectedAddressIndex(tempSelectedAddressIndex);

    if (tempSelectedAddressIndex !== 0) {
      const updatedAddresses = [...addresses];
      const selected = updatedAddresses[tempSelectedAddressIndex];
      updatedAddresses.splice(tempSelectedAddressIndex, 1);
      updatedAddresses.unshift(selected);
      setAddresses(updatedAddresses);
      setSelectedAddressIndex(0);
    }

    setOpen(false);
  };

  const handleAddNewAddress = () => {
    setOpen(false);
    setShowAddNewForm(true);
  };

  const toggleAddressOptions = () => {
    setTempSelectedAddressIndex(selectedAddressIndex);
    setOpen(true);
  };

  useEffect(() => {
    function checkPayVisibility() {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener("scroll", checkPayVisibility, { passive: true });
    window.addEventListener("resize", checkPayVisibility);
    const timeoutId = setTimeout(checkPayVisibility, 50);
    return () => {
      window.removeEventListener("scroll", checkPayVisibility);
      window.removeEventListener("resize", checkPayVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [summary, cartItems.length, isLoading]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsLoading(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const saveAddress = async (address) => {
    const updatedAddresses = [address, ...addresses];
    setAddresses(updatedAddresses);
    setSelectedAddressIndex(0);

    analyticsHelper.identify(
      { ...user, email: user.email || address.email },
      () => analyticsHelper.addedAddress(address),
    );

    await updateDoc(doc(firestore, "Users/UsersList/DataBase", user.uid), {
      addresses: updatedAddresses,
    });

    if (user?.email) {
      setShowAddNewForm(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await fetchCheckoutData();
        setHasFetchedData(true);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!loading && !hasFetchedData && (user?.uid || !user) && cartID) {
      loadData();
    }
  }, [user?.uid, cartID, firestore, collectionPath, loading, hasFetchedData]);

  useEffect(() => {
    if (isMounted) return noop;
    if (cartItems.length > 0 && summary.total > 0.01 && wallet) {
      analyticsHelper.startedCheckout({
        cartItems,
        summary,
        cart_sooperstock_ID,
        wallet,
      });
      setIsMounted(true);
    }
  }, [isMounted, cartItems.length, cart_sooperstock_ID, summary.total, wallet]);

  const fetchCheckoutData = async () => {
    try {
      if (!user || !cartID) return;
      const cartDocRef = doc(
        firestore,
        `${collectionPath}Carts/CartsList/DataBase`,
        cartID,
      );
      await updateDoc(cartDocRef, { opening_checkout_page: new Date() });
    } catch (error) {
      console.error("Error fetching checkout data:", error);
    }
  };

  useEffect(() => {
    const fetchAddresses = () => {
      if (!loading && !user?.email) {
        setShowAddNewForm(true);
      } else if (!loading && user?.addresses?.length) {
        setAddresses(user.addresses);
        setShowAddNewForm(false);
      } else if (!loading && (!user?.addresses || user.addresses.length === 0)) {
        setShowAddNewForm(true);
      }
    };
    if (!loading) fetchAddresses();
  }, [loading, user?.email, user?.addresses]);

  const saveShippingOption = async (shippingData) => {
    const docRef = doc(db, `${collectionPath}Carts/CartsList/DataBase`, cartID);
    await updateDoc(docRef, {
      shippingOption: shippingData.shippingOption || null,
      shippingCost: shippingData?.shippingCost || 0,
      shipping_cost: shippingData?.shippingCost || 0,
      free_shipping_threshold: shippingData?.shippingOption?.free_threshold || 0,
    });
  };

  useEffect(() => {
    if (!refreshedAtTime) return () => {};
    if (!cartID) return () => {};
    const checkCart = async () => {
      const cartErrors = await validateCart(cartID);
      if (cartErrors.length > 0) navigate("/cart");
    };
    checkCart();
  }, [refreshedAtTime]);

  const handleSubmitRequest = async () => {
    setIsLoading(true);
    setShowConfirmModal(false);

    const cartErrors = await validateCart(cartID);
    if (cartErrors.length > 0) {
      navigate("/cart");
      return;
    }

    try {
      await saveShippingOption(selectedShippingData);

      if (shippingAddress && shippingAddressValid) {
        await saveAddress(shippingAddress);
      }

      const finalAddress = shippingAddressValid
        ? shippingAddress
        : addresses[selectedAddressIndex];

      let currentEmail = user?.email || finalAddress?.email;
      if (!currentEmail) {
        alert("Please provide your email address to finish your order.");
        setIsLoading(false);
        return;
      }

      if (finalAddress && !finalAddress.email) {
        finalAddress.email = currentEmail;
      }

      await updateDoc(
        doc(firestore, `${collectionPath}Carts/CartsList/DataBase`, cartID),
        {
          shipmentAddress: finalAddress,
          paymentType: "Manual",
          manualPaymentMethod: paymentMethod,
          isAnonymous: user?.isAnonymous || false,
          walletDeduction: summary.paymentTotals.wallet || 0,
          paid_amount_total: Math.round(summary.total * 100),
        },
      );

      const data = await apiClient.postData(
        "/payment/manualPayment",
        { cartID, paymentMethod },
        user,
      );

      if (data.success) {
        navigate(`/payment-request?cart_id=${cartID}`, { replace: true });
      } else {
        alert(data.error || "Failed to submit order. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Manual payment error:", error);
      alert("Failed to submit order. Please try again.");
      setIsLoading(false);
    }
  };

  const disabled =
    !selectedAddress ||
    !selectedShippingData ||
    isLoading ||
    (showAddNewForm && !shippingAddressValid);

  const selectedMethodHandle =
    paymentMethod === "zelle" ? zelleHandle : venmoHandle;
  const selectedMethodLabel =
    paymentMethod === "zelle" ? "Zelle" : "Venmo";

  const SubmitButton = ({ style }) => (
    <FPButton
      $dark
      onClick={() => setShowConfirmModal(true)}
      disabled={disabled}
      fontSize={"1.2rem"}
      padding={"10px"}
      background={"#3b72fe"}
      outlined={false}
      textColor={"white"}
      style={{ maxWidth: 600, width: "100%", ...style }}
    >
      {isLoading ? "Submitting..." : "Submit Request"}
    </FPButton>
  );

  return (
    <div style={{ backgroundColor: "white" }}>
      <Header search={false} />
      <div
        css={{
          maxWidth: "1140px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          paddingTop: 16,
        }}
      >
        <ShippingCheckoutBanner totalAmount={summary.subtotal} />

        {!user?.email && <CheckoutAuthBanner onSignInComplete={refresh} />}

        <div style={styles.checkoutContainer}>
          <h2 style={styles.orderTitle}>Shipping Details</h2>
          {!showAddNewForm && user?.email && (
            <div style={styles.address}>
              {addresses?.length > 0 ? (
                <AddressBox addressObject={addresses[selectedAddressIndex]}>
                  <button
                    style={styles.linkButton}
                    onClick={toggleAddressOptions}
                  >
                    <CreateOutlined
                      style={{ fontSize: ".8rem", position: "relative", top: 2 }}
                    />{" "}
                    Change Address
                  </button>
                </AddressBox>
              ) : (
                <button style={styles.linkButton} onClick={toggleAddressOptions}>
                  <CreateOutlined
                    style={{ fontSize: ".8rem", position: "relative", top: 2 }}
                  />{" "}
                  Please add an address
                </button>
              )}
            </div>
          )}
          {(showAddNewForm || !user?.email) && (
            <div>
              <AddressForm
                existingEmail={user?.email}
                onSave={saveAddress}
                onCancel={() => setShowAddNewForm(false)}
                showSaveButton={false}
                showCancelButton={addresses.length > 0 && user?.email}
                onChange={({ address, valid }) => {
                  setShippingAddress(address);
                  setShippingAddressValid(valid);
                }}
              />
            </div>
          )}

          <div>
            <h3 style={{ fontSize: 18 }}>Shipping Speed</h3>
            <p
              style={{
                fontSize: 13,
                color: "#666",
                lineHeight: 1.3,
                margin: "8px 0 16px 0",
              }}
            >
              Exact delivery timelines vary by address. Orders ship next
              business day except on holidays (*Note: Fri-Sun orders ship on
              Mon).
            </p>
            <ShippingOptions
              cartPrice={summary.subtotal}
              selectedSlug={selectedShippingData?.shippingOption?.slug}
              onChange={setSelectedShippingData}
            />
          </div>

          <div>
            <h3 style={{ fontSize: 18 }}>Payment Method</h3>
            <p style={{ fontSize: 13, color: "#666", margin: "8px 0 16px 0" }}>
              Submit your order now and send payment after — your order ships
              once we confirm receipt.
            </p>

            {[
              {
                id: "zelle",
                label: "Zelle",
                handle: zelleHandle,
              },
              {
                id: "venmo",
                label: "Venmo",
                handle: venmoHandle,
              },
            ].map((method) => (
              <div
                key={method.id}
                style={{
                  border:
                    paymentMethod === method.id
                      ? "2px solid #3B72FE"
                      : "1px solid #ddd",
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 10,
                  cursor: "pointer",
                }}
                onClick={() => setPaymentMethod(method.id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border:
                        paymentMethod === method.id
                          ? "2px solid #3B72FE"
                          : "2px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {paymentMethod === method.id && (
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#3B72FE",
                        }}
                      />
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{method.label}</div>
                    {paymentMethod === method.id && method.handle && (
                      <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                        Send to: <strong>{method.handle}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div
              style={{
                background: "#fff8e1",
                border: "1px solid #ffe082",
                borderRadius: 8,
                padding: "12px 16px",
                marginTop: 8,
                fontSize: 13,
                color: "#555",
              }}
            >
              After submitting, send payment via {selectedMethodLabel} to{" "}
              <strong>{selectedMethodHandle}</strong> and include your Order #
              in the memo. We confirm within minutes during business hours
              (Mon–Fri, 9am–5pm PT).
            </div>
          </div>
        </div>

        <div>
          <div style={styles.summary}>
            <Summary summary={summary} appliedDiscount={appliedDiscount} />
            <div ref={payButtonRef} style={{ height: 30 }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <SubmitButton />
            </div>
          </div>
        </div>
      </div>
      <FAQ />
      <Footer />

      {showStickyFooter && (
        <StickyFooter>
          <div
            style={{
              display: "flex",
              margin: "10px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <SubmitButton style={{ maxWidth: 600 }} />
          </div>
        </StickyFooter>
      )}

      {/* Confirmation modal */}
      {showConfirmModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 16,
              padding: "24px 20px 28px",
              width: "100%",
              maxWidth: 480,
              margin: "0 16px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>
              Before you submit
            </h3>
            <p style={{ margin: "0 0 12px 0", fontSize: 15, lineHeight: 1.5, color: "#444" }}>
              After submitting, you&apos;ll receive an Order # — then send
              payment via <strong>{selectedMethodLabel}</strong> to{" "}
              <strong>{selectedMethodHandle}</strong> and include your{" "}
              <strong>Order #</strong> in the memo. Your order ships once we
              confirm payment.
            </p>
            <p style={{ margin: "0 0 24px 0", fontSize: 13, color: "#888" }}>
              Questions?{" "}
              <a
                href="mailto:support@getfullproof.com"
                style={{ color: "#007aff" }}
              >
                support@getfullproof.com
              </a>
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: 50,
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontSize: 16,
                }}
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 2,
                  padding: "12px",
                  border: "none",
                  borderRadius: 50,
                  backgroundColor: "#3b72fe",
                  color: "white",
                  cursor: "pointer",
                  fontSize: 16,
                  fontWeight: 600,
                }}
                onClick={handleSubmitRequest}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address selection sheet */}
      <Sheet isOpen={isOpen} onClose={() => setOpen(false)}>
        <Sheet.Container>
          <Sheet.Header>
            <div style={{ padding: "15px 20px 0 20px" }}>
              <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Change Address
              </div>
              <div
                style={{
                  marginTop: 15,
                  width: "100%",
                  fontWeight: 400,
                  fontSize: ".9rem",
                }}
              >
                Select the delivery address:
              </div>
            </div>
          </Sheet.Header>
          <div style={{ padding: "0 20px" }}>
            {addresses.map((addr, index) => (
              <div
                key={index}
                style={{ cursor: "pointer" }}
                onClick={() => handleAddressSelect(index)}
              >
                <AddressBox
                  addressObject={addr}
                  selected={tempSelectedAddressIndex === index}
                />
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                style={{
                  backgroundColor: "white",
                  color: "black",
                  padding: "8px 16px",
                  border: "1px solid black",
                  marginTop: "10px",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  borderRadius: "50px",
                }}
                onClick={handleAddNewAddress}
              >
                <Add
                  style={{
                    fontSize: ".9rem",
                    position: "relative",
                    top: 2,
                    marginRight: 5,
                  }}
                />
                Add new address
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                style={{
                  backgroundColor: "black",
                  color: "white",
                  padding: "8px 16px",
                  border: "none",
                  width: "100%",
                  margin: "10px auto 30px auto",
                  cursor: "pointer",
                  fontSize: "16px",
                  borderRadius: "50px",
                  maxWidth: "340px",
                }}
                onClick={handleSaveAddressChange}
              >
                Save Changes
              </button>
            </div>
          </div>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setOpen(false)} />
      </Sheet>
    </div>
  );
};

const styles = {
  checkoutContainer: {
    width: "100%",
    padding: "0 20px",
    fontFamily: "'Inter', sans-serif",
  },
  orderTitle: { fontSize: "18px", marginBottom: "12px" },
  address: { marginBottom: "20px" },
  linkButton: {
    fontSize: ".8rem",
    color: "black",
    cursor: "pointer",
    border: "1px solid black",
    borderRadius: "50px",
    padding: "5px 10px",
    marginTop: "10px",
    backgroundColor: "white",
  },
  summary: {
    marginTop: "10px",
    padding: "10px 15px 40px",
    background: "rgba(59, 114, 254, 0.05)",
  },
};

export default ManualCheckout;
